import { useRef, useState, useEffect } from 'react';
import { formaterPrix } from '../../../utils/formatPrix';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../../config';

import { Sparklines, SparklinesLine } from 'react-sparklines';

// hooks
import useClickOutside from '../../../hooks/useClickOutside';

// interfaces
interface IProps {
  item: any;
  index: number;
  isFavorited?: boolean;
  onToggleFavorite?: (symbol: string, isFav: boolean) => void;
}

const CapitalRow: React.FC<IProps> = ({ item, index, isFavorited = false, onToggleFavorite }) => {
  const ref = useRef<any>(null);
  const navigate = useNavigate();

  const [color, setColor] = useState<string>('');
  const [menuOpened, setMenuOpened] = useState<boolean>(false);
  const [imgError, setImgError] = useState<boolean>(false);

  useClickOutside(ref, () => setMenuOpened(false));

  useEffect(() => {
    if (item.status === 1) {
      setColor('green');
    } else {
      setColor('red');
    }
  }, [item.status]);

  /**
   * Handle row click to navigate to stock details
   */
  const handleRowClick = (e: React.MouseEvent): void => {
    // Don't navigate if clicking on action button
    if ((e.target as HTMLElement).closest('.action-cell')) {
      return;
    }
    navigate(`/data?symbol=${item.symbol}`);
  };

  /**
   * Toggles the state of the menu to open or close.
   */
  const handleMenuOpen = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setMenuOpened(!menuOpened);
  };

  /**
   * Toggles favorite status for this stock
   */
  const handleToggleFavorite = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    const token = localStorage.getItem('access_token');
    if (!token) return;

    try {
      if (isFavorited) {
        await fetch(`${API_URL}/api/favorites/${item.symbol}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (onToggleFavorite) onToggleFavorite(item.symbol, false);
      } else {
        await fetch(`${API_URL}/api/favorites/${item.symbol}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (onToggleFavorite) onToggleFavorite(item.symbol, true);
      }
      setMenuOpened(false);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Removed inline background styles so CSS handles dark mode natively
  const rowStyle = {
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  };

  return (
    <tr
      style={rowStyle}
      onClick={handleRowClick}
    >
      <td className='nowrap' style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Logo Display Logic */}
          {!imgError ? (
            <img
              src={`/images/societes/${item.symbol}.png`}
              alt={item.symbol}
              onError={(e) => {
                // Try jpg if png fails (just in case) or set error
                const target = e.target as HTMLImageElement;
                if (target.src.endsWith('.png')) {
                  // Optional: could try .jpg, but for now just fail to fallback
                  setImgError(true);
                } else {
                  setImgError(true);
                }
              }}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                objectFit: 'contain', // Ensure logo fits well
                backgroundColor: 'white', // White bg for transparent logos
                boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                border: '1px solid #f1f5f9'
              }}
            />
          ) : (
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
              }}
            >
              {item.symbol?.substring(0, 4) || ''}
            </div>
          )}
          <div>
            <div style={{ fontWeight: '600', color: 'var(--text-heading)', marginBottom: '2px' }}>
              {item.name || item.symbol}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              {item.symbol}
            </div>
          </div>
        </div>
      </td>
      <td className='center' style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
        <strong style={{ fontSize: '15px', color: 'var(--text-heading)' }}>
          {formaterPrix(item.amount)} {item.currency}
        </strong>
      </td>
      <td className='center' style={{ padding: '12px 16px' }}>
        <strong style={{ fontSize: '14px', color: 'var(--text-main)' }}>
          {item.volume_montant
            ? `${(item.volume_montant / 1000000).toFixed(1)} M`
            : '-'}
        </strong>
      </td>
      <td className='center' style={{ padding: '12px 16px' }}>
        <span
          style={{
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: '600',
            background: color === 'green' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            color: color === 'green' ? '#10b981' : '#ef4444',
          }}
        >
          {item.change}
        </span>
      </td>
      <td aria-label='chart' className='responsive-hide' style={{ padding: '12px 16px' }}>
        <div className='line-chart'>
          <Sparklines data={item.lineChartData} width={120} height={40}>
            <SparklinesLine style={{ strokeWidth: 3 }} color={color === 'green' ? '#10b981' : '#ef4444'} />
          </Sparklines>
        </div>
      </td>
      <td ref={ref} className='right action-cell' style={{ padding: '12px 16px' }}>
        <button
          type='button'
          className='pointer'
          onClick={handleMenuOpen}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '8px',
          }}
        >
          <i className='material-icons'>more_vert</i>
        </button>

        {menuOpened && (
          <div className='box-dropdown'>
            <ul>
              <li>
                <button type='button' onClick={() => navigate(`/data?symbol=${item.symbol}`)}>
                  <i className='material-icons'>show_chart</i>
                  Voir détails
                </button>
              </li>
              <li>
                <button type='button' onClick={handleToggleFavorite}>
                  <i className='material-icons'>{isFavorited ? 'star' : 'star_border'}</i>
                  {isFavorited ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                </button>
              </li>
            </ul>
          </div>
        )}
      </td>
    </tr >
  );
};

export default CapitalRow;
